import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import HelcimCardForm from '../components/HelcimCardForm';
import { useCart } from '../context/CartContext';
import StripeProvider from '../components/StripeProvider';
import { useToast } from '../components/ui';
import SEO from '../components/SEO';
import HotmailDeliveryNotice from '../components/HotmailDeliveryNotice';
import WalletPay from '../components/WalletPay';
import './Checkout.css';

const STRIPE_ENABLED = !!process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

const EnhancedCheckout = () => {
  // The whole page wraps in StripeProvider so PaymentForm has Elements context
  return (
    <StripeProvider>
      <CheckoutInner />
    </StripeProvider>
  );
};

function CheckoutInner() {
  const { cart, getCartTotal, clearCart } = useCart();
  const navigate = useNavigate();
  const toast = useToast();
  const stripe = useStripe();
  const elements = useElements();
  const [user, setUser] = useState(null);
  const [checkoutAs, setCheckoutAs] = useState('guest');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [placedOrder, setPlacedOrder] = useState(null); // Sprint 4: rich confirmation panel

  // Discount state (B2, B3, B4)
  const [couponCode, setCouponCode]       = useState('');
  const [couponDiscount, setCouponDiscount] = useState(0);
  // Tier 3 Item 35 — stacked secondary coupons. Each entry: { code, discount }
  const [stackedCoupons, setStackedCoupons] = useState([]);
  const [stackingFlagOn, setStackingFlagOn] = useState(false);
  const [giftCardCode, setGiftCardCode]   = useState('');
  const [giftCardApplied, setGiftCardApplied] = useState(0);
  const [loyaltyPoints, setLoyaltyPoints] = useState(0);     // points entered to redeem
  const [availablePoints, setAvailablePoints] = useState(0); // user's balance
  const [stripeReady, setStripeReady] = useState(false);     // card element complete
  const [provider, setProvider] = useState('stripe');        // 'stripe' | 'helcim' | 'moneris' (admin-selected)
  const [walletEnabled, setWalletEnabled] = useState(false);
  const [sezzleEnabled, setSezzleEnabled] = useState(false);
  useEffect(() => {
    fetch('/api/payments/active-provider')
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.provider) setProvider(String(d.provider).toLowerCase()); })
      .catch(() => {});
    fetch('/api/feature-flags/public')
      .then(r => r.ok ? r.json() : {})
      .then(flags => {
        setWalletEnabled(!!(flags && flags.wallet_pay_enabled));
        setSezzleEnabled(!!(flags && flags.payments_sezzle_enabled));
        setStackingFlagOn(!!(flags && flags.coupons_stackable_enabled));
      })
      .catch(() => {});
  }, []);

  // Tier 1 Item 1 — live shipping rates (Canada Post + future Stallion/ChitChats).
  // Debounced fetch when postal+country are valid, gated server-side by flags.
  const [liveRates, setLiveRates] = useState([]);            // [{provider, service_code, service_name, total_cents, eta_days}]
  const [selectedRate, setSelectedRate] = useState(null);    // the chosen rate, or null = use flat rules
  const [liveRatesLoading, setLiveRatesLoading] = useState(false);
  const [liveRatesErr, setLiveRatesErr] = useState(null);

  // Tier 1 Item 13 — "Ships from" indicator. Fires whenever postal + cart settle.
  const [shipsFrom, setShipsFrom] = useState(null);
  useEffect(() => {
    const postal = String(formData.shipping_postal_code || '').replace(/\s+/g, '');
    if (postal.length < 5 || !cart.length) { setShipsFrom(null); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const r = await fetch('/api/shipping/ships-from', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: cart.map(i => ({ product_id: i.id, quantity: i.quantity })),
            destination: { province: formData.shipping_province, country: formData.shipping_country, postal_code: postal },
          }),
        });
        if (r.ok && !cancelled) setShipsFrom(await r.json());
      } catch (_) {}
    }, 600);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.shipping_postal_code, formData.shipping_province]);

  // Tier 1 Item 7 — Net-30 eligibility (B2B-only). Loaded once on mount when
  // user is logged in. The Net-30 radio is hidden if not eligible.
  const [net30, setNet30] = useState({ eligible: false, default_terms_days: 30 });
  const [paymentMode, setPaymentMode] = useState('card'); // 'card' | 'net30'
  // Tier 2 Item 21 — BOPIS fulfillment toggle
  const [fulfillmentMode, setFulfillmentMode] = useState('ship'); // 'ship' | 'pickup'
  const [bopisAvailable, setBopisAvailable] = useState(false);
  useEffect(() => {
    fetch('/api/site/feature-flags').then(r => r.ok ? r.json() : null)
      .then(flags => {
        const arr = Array.isArray(flags) ? flags : Object.values(flags || {});
        const on = arr.some(f => (f.feature_key === 'bopis_enabled' && f.enabled));
        setBopisAvailable(on);
      }).catch(() => {});
  }, []);
  const [poNumber, setPoNumber] = useState('');
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch('/api/orders/me/net30-eligibility', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setNet30(d); })
      .catch(() => {});
  }, []);

  // Refetch when postal/country/cart changes. Server returns [] when the master
  // flag is off, so checkout silently falls back to the flat rule.
  useEffect(() => {
    const postal = String(formData.shipping_postal_code || '').replace(/\s+/g, '');
    if (postal.length < 5 || !cart.length) { setLiveRates([]); setSelectedRate(null); return; }
    let cancelled = false;
    setLiveRatesLoading(true); setLiveRatesErr(null);
    const t = setTimeout(async () => {
      try {
        const r = await fetch('/api/shipping/quote/live', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: cart.map(i => ({ product_id: i.id, quantity: i.quantity })),
            destination: { postal, country: formData.shipping_country === 'Canada' ? 'CA' : formData.shipping_country },
          }),
        });
        if (cancelled) return;
        if (!r.ok) { setLiveRates([]); setLiveRatesErr('Live rates unavailable'); return; }
        const rates = await r.json();
        setLiveRates(Array.isArray(rates) ? rates : []);
        // Auto-select the cheapest if nothing chosen yet
        if (Array.isArray(rates) && rates.length && !selectedRate) {
          const cheapest = rates.slice().sort((a, b) => a.total_cents - b.total_cents)[0];
          setSelectedRate(cheapest);
        }
      } catch (_) { if (!cancelled) setLiveRatesErr('Network error'); }
      finally { if (!cancelled) setLiveRatesLoading(false); }
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.shipping_postal_code, formData.shipping_country, cart.map(c => `${c.id}:${c.quantity}`).join(',')]);
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    shipping_address_line1: '',
    shipping_address_line2: '',
    shipping_city: '',
    shipping_province: 'Ontario',
    shipping_postal_code: '',
    shipping_country: 'Canada',
    order_notes: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');

    let parsedUser = null;
    if (token && userData) {
      try { parsedUser = JSON.parse(userData); }
      catch { try { localStorage.removeItem('user'); } catch {} }
    }
    if (parsedUser) {
      setUser(parsedUser);
      setCheckoutAs('user');

      setFormData(prev => ({
        ...prev,
        customer_name: `${parsedUser.first_name || ''} ${parsedUser.last_name || ''}`.trim(),
        customer_email: parsedUser.email || '',
        customer_phone: parsedUser.phone || '',
      }));

      // Fetch loyalty balance for this user
      fetch(`/api/loyalty/user/${parsedUser.id}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (d) setAvailablePoints(d.available_points || 0); })
        .catch(() => {});
    }
  }, []);

  // First-purchase auto-coupon hint (NEW10 if they haven't ordered before)
  const [welcomeCoupon, setWelcomeCoupon] = useState(null);
  useEffect(() => {
    const email = formData.customer_email;
    if (!email || !email.includes('@')) { setWelcomeCoupon(null); return; }
    fetch(`/api/coupons/welcome?email=${encodeURIComponent(email)}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.eligible) setWelcomeCoupon(d); else setWelcomeCoupon(null); })
      .catch(() => {});
  }, [formData.customer_email]);

  // Apply / remove coupon
  const applyCoupon = async () => {
    if (!couponCode) return;
    try {
      const r = await fetch('/api/coupons/validate', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: couponCode, subtotal: getCartTotal() }),
      });
      const data = await r.json();
      if (!r.ok || data.valid === false) throw new Error(data.error || 'Invalid coupon');
      setCouponDiscount(Number(data.discount_amount || data.discount || 0));
      toast.success(`Coupon ${couponCode.toUpperCase()} applied — saved $${Number(data.discount_amount || 0).toFixed(2)}`);
    } catch (e) {
      setCouponDiscount(0);
      toast.error(e.message);
    }
  };

  // Apply / remove gift card
  const applyGiftCard = async () => {
    if (!giftCardCode) return;
    try {
      const r = await fetch(`/api/gift-cards/${encodeURIComponent(giftCardCode)}/balance`);
      const data = await r.json();
      if (!r.ok || !data.current_balance) throw new Error(data.error || 'Invalid gift card');
      setGiftCardApplied(Number(data.current_balance));
      toast.success(`Gift card has $${Number(data.current_balance).toFixed(2)} available`);
    } catch (e) {
      setGiftCardApplied(0);
      toast.error(e.message);
    }
  };

  const loyaltyDiscount = Math.min(loyaltyPoints, availablePoints) / 100;

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const provinces = [
    'Alberta', 'British Columbia', 'Manitoba', 'New Brunswick',
    'Newfoundland and Labrador', 'Northwest Territories', 'Nova Scotia',
    'Nunavut', 'Ontario', 'Prince Edward Island', 'Quebec',
    'Saskatchewan', 'Yukon'
  ];

  const calculateTotals = () => {
    const subtotal = getCartTotal();
    // Live carrier rate wins when chosen; otherwise fall back to the flat
    // "free over $100, else $10" rule. Live rate stored in cents.
    const shipping = selectedRate
      ? Number(selectedRate.total_cents) / 100
      : (subtotal >= 100 ? 0 : 10);

    // Tax rates by province
    const taxRates = {
      'Ontario': 0.13,
      'Quebec': 0.14975,
      'British Columbia': 0.12,
      'Alberta': 0.05,
      'Manitoba': 0.12,
      'Saskatchewan': 0.11,
      'Nova Scotia': 0.15,
      'New Brunswick': 0.15,
      'Newfoundland and Labrador': 0.15,
      'Prince Edward Island': 0.15,
      'Northwest Territories': 0.05,
      'Yukon': 0.05,
      'Nunavut': 0.05
    };

    const taxRate = taxRates[formData.shipping_province] || 0.13;
    const tax = subtotal * taxRate;
    const baseTotal = subtotal + shipping + tax;

    // Apply discounts (cap so total never goes negative)
    const giftCardCap = Math.min(giftCardApplied, Math.max(baseTotal - couponDiscount - loyaltyDiscount, 0));
    const discount = couponDiscount + giftCardCap + loyaltyDiscount;
    const total = Math.max(0, baseTotal - discount);

    return { subtotal, shipping, tax, baseTotal, discount, total, couponDiscount, giftCardApplied: giftCardCap, loyaltyDiscount };
  };

  const placeOrder = async ({ paymentIntentId, provider: payProvider } = {}) => {
    const totals = calculateTotals();
    const headers = { 'Content-Type': 'application/json' };
    const token = localStorage.getItem('token');
    if (token && checkoutAs === 'user') headers['Authorization'] = `Bearer ${token}`;

    const orderData = {
      ...formData,
      items: cart.map(item => ({ product_id: item.id, quantity: item.quantity })),
      payment_intent_id: paymentIntentId,
      payment_provider: payProvider,
      payment_method: paymentMode === 'net30' ? 'net30' : (paymentMode === 'sezzle' ? 'sezzle' : undefined),
      po_number: paymentMode === 'net30' ? (poNumber || undefined) : undefined,
      net_terms_days: paymentMode === 'net30' ? net30.default_terms_days : undefined,
      fulfillment_type: fulfillmentMode === 'pickup' ? 'pickup' : undefined,
      shipping_rate: selectedRate ? {
        provider: selectedRate.provider,
        service_code: selectedRate.service_code,
        service_name: selectedRate.service_name,
        total_cents: selectedRate.total_cents,
      } : null,
      // Stackable coupons: send array when multiple are applied; keep legacy single
      // field too for backward-compat with non-stacking deploys.
      coupon_code: couponDiscount > 0 ? couponCode : undefined,
      coupon_codes: stackedCoupons.length > 0
        ? [couponCode, ...stackedCoupons.map(s => s.code)]
        : undefined,
      gift_card_code: giftCardApplied > 0 ? giftCardCode : undefined,
      loyalty_redeem_points: loyaltyDiscount > 0 ? Math.min(loyaltyPoints, availablePoints) : undefined,
      estimated_total: totals.total,
    };

    const r = await fetch('/api/orders', { method: 'POST', headers, body: JSON.stringify(orderData) });
    const data = await r.json();
    if (!r.ok) {
      throw new Error((data.errors && data.errors.join('. ')) || data.error || 'Order failed');
    }

    // Tier 3 Item 32 — BNPL handoff. Order is created in 'pending'; we now
    // create a Sezzle session referencing the order id and redirect out.
    // Webhook flips the order to 'paid' once Sezzle captures.
    if (paymentMode === 'sezzle') {
      const sessionRes = await fetch('/api/payments/sezzle/session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: totals.total,
          currency: 'CAD',
          order_reference: String(data.orderId || data.order_id),
          customer: {
            email: formData.customer_email,
            first_name: (formData.customer_name || '').split(' ')[0],
            last_name:  (formData.customer_name || '').split(' ').slice(1).join(' '),
            phone: formData.customer_phone,
          },
          items: cart.map(it => ({
            name: it.name, sku: it.id, quantity: it.quantity, price: it.price,
          })),
        }),
      });
      const session = await sessionRes.json();
      if (!sessionRes.ok || !session.redirect_url) {
        throw new Error(session.error || 'Failed to create Sezzle session');
      }
      // Persist cart so customer sees confirmation on return — order itself
      // is already saved server-side; we just don't clear cart until paid.
      window.location.href = session.redirect_url;
      return;
    }

    clearCart();
    // Sprint 4: stash the placed-order info so the inline confirmation panel
    // can show next-steps, ship estimate, and "track your order". No more
    // 2.5s auto-redirect — let the customer read what happened.
    const orderRef = data.orderId || data.order_id;
    const couponLines = Array.isArray(data.applied_coupons) && data.applied_coupons.length
      ? data.applied_coupons.map(c => `${c.code} (−$${Number(c.amount).toFixed(2)})`).join(', ')
      : '';
    setPlacedOrder({
      id: orderRef,
      couponLines,
      shipDateEstimate: estimateShipDate(),
      isLoggedIn: !!user,
    });
    toast.success(`Order #${orderRef} placed!`);
  };

  // Ship-date estimate: orders placed before 2pm ET ship same business day,
  // else next business day. Plus 1-7 business days transit. Returns a
  // human-friendly range string.
  function estimateShipDate() {
    const now = new Date();
    const hour = now.getHours();
    const cutoff = hour < 14;
    const offset = cutoff ? 0 : 1;
    // Skip weekends.
    function addBusinessDays(d, days) {
      const out = new Date(d);
      let added = 0;
      while (added < days) {
        out.setDate(out.getDate() + 1);
        if (out.getDay() !== 0 && out.getDay() !== 6) added++;
      }
      return out;
    }
    const shipDate = addBusinessDays(now, offset);
    const fmt = (d) => d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
    return cutoff ? `today (${fmt(now)})` : `${fmt(shipDate)}`;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    const totals = calculateTotals();
    try {
      // Demo mode (no provider configured) OR zero-total OR non-Stripe provider
      // (Helcim is handled separately via the HelcimCardForm onSuccess hook)
      // OR Net-30 (no card up front).
      if (paymentMode === 'net30' || paymentMode === 'sezzle' || provider !== 'stripe' || !STRIPE_ENABLED || totals.total === 0) {
        await placeOrder();
        return;
      }

      // Stripe configured — two-phase: create intent, confirm card, place order
      if (!stripe || !elements) {
        throw new Error('Payment system not ready. Please refresh and try again.');
      }
      const card = elements.getElement(CardElement);
      if (!card) throw new Error('Please enter your card details.');

      // 1. Create payment intent for the discounted total
      const intentRes = await fetch('/api/payments/create-payment-intent', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: totals.total, currency: 'cad', userId: user?.id || null }),
      });
      const intent = await intentRes.json();
      if (!intentRes.ok) throw new Error(intent.error || 'Failed to start payment');

      // 2. Confirm card payment
      const { error: stripeErr, paymentIntent } = await stripe.confirmCardPayment(intent.clientSecret, {
        payment_method: {
          card,
          billing_details: { name: formData.customer_name, email: formData.customer_email },
        },
      });
      if (stripeErr) throw new Error(stripeErr.message);
      if (paymentIntent.status !== 'succeeded') {
        throw new Error(`Payment status: ${paymentIntent.status}`);
      }

      // 3. Create order with the verified payment intent id
      await placeOrder({ paymentIntentId: paymentIntent.id });
    } catch (err) {
      setError(err.message || 'Network error. Please try again.');
      toast.error(err.message || 'Order failed');
    } finally {
      setLoading(false);
    }
  };

  if (cart.length === 0) {
    return (
      <div className="checkout-empty">
        <h2>Your cart is empty</h2>
        <p>Add Pokemon cards to your cart to continue.</p>
        <Link to="/shop" className="btn btn-primary">
          Browse Products
        </Link>
      </div>
    );
  }

  const totals = calculateTotals();

  // Sprint 2.5: deposit-mode detection. If every cart item came in via the
  // PDP "Reserve with deposit" CTA, charge the sum of deposits instead of the
  // full cart total — Helcim runs as preauth so we can capture the balance
  // later when stock ships.
  const depositItems = cart.filter((it) => it.preauth_mode && Number(it.preauth_amount) > 0);
  const isDepositCheckout = depositItems.length > 0 && depositItems.length === cart.length;
  const depositTotal = depositItems.reduce((sum, it) => sum + Number(it.preauth_amount) * (it.quantity || 1), 0);
  const chargeAmount = isDepositCheckout ? depositTotal : totals.total;
  const hasMixedDepositCart = depositItems.length > 0 && depositItems.length !== cart.length;

  return (
    <div className="checkout">
      <SEO title="Checkout" description="Secure checkout — Stripe + Apple Pay + Google Pay. Tracked shipping across Canada." noindex />
      <h1>Checkout</h1>

      {!user && (
        <div className="checkout-options">
          <h2>How would you like to checkout?</h2>
          <div className="option-cards">
            <button
              className={`option-card ${checkoutAs === 'guest' ? 'active' : ''}`}
              onClick={() => setCheckoutAs('guest')}
              aria-pressed={checkoutAs === 'guest'}
            >
              <h3>Guest Checkout</h3>
              <p>Quick and easy, no account needed</p>
            </button>

            <button
              className={`option-card ${checkoutAs === 'login' ? 'active' : ''}`}
              onClick={() => navigate('/login', { state: { from: '/checkout' } })}
            >
              <h3>Sign In</h3>
              <p>Faster checkout and order tracking</p>
            </button>

            <button
              className={`option-card ${checkoutAs === 'register' ? 'active' : ''}`}
              onClick={() => navigate('/register')}
            >
              <h3>Create Account</h3>
              <p>Save addresses and track orders</p>
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="error" role="alert" aria-live="polite">
          {error}
        </div>
      )}

      {placedOrder && (
        <div
          role="alert"
          aria-live="polite"
          style={{
            border: '1px solid #0d5c3a', background: '#f0fdf4',
            padding: 20, borderRadius: 8, margin: '12px 0 20px',
          }}
        >
          <h2 style={{ marginTop: 0, color: '#0d5c3a' }}>Thanks! Order #{placedOrder.id} is placed.</h2>
          <p style={{ marginBottom: 8 }}>
            A confirmation email is on its way to <strong>{formData.customer_email}</strong>. Check your spam folder if it doesn't appear within a few minutes — and add us to your contacts so future updates land in your inbox.
          </p>
          <ul style={{ paddingLeft: 18, fontSize: 14, lineHeight: 1.6 }}>
            <li><strong>Estimated ship date:</strong> {placedOrder.shipDateEstimate} (1–7 business days transit across Canada)</li>
            {placedOrder.couponLines && <li><strong>Coupons applied:</strong> {placedOrder.couponLines}</li>}
            <li><strong>What's next:</strong> we'll email you a tracking number once your package is on the truck.</li>
          </ul>
          <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
            {placedOrder.isLoggedIn ? (
              <Link to="/account/orders" className="btn btn-primary">Track your order →</Link>
            ) : (
              <Link to="/account/login" className="btn btn-primary">Create an account to track →</Link>
            )}
            <Link to="/shop" className="btn">Keep browsing</Link>
          </div>
        </div>
      )}
      {!placedOrder && success && (
        <div className="success" role="alert" aria-live="polite">
          {success}
        </div>
      )}

      <div className="checkout-content">
        <div className="checkout-form">
          <h2>Shipping Information</h2>

          <form onSubmit={handleSubmit} aria-label="Checkout form">
            <div className="form-group">
              <label htmlFor="customer_name">
                Full Name <span className="required" aria-label="required">*</span>
              </label>
              <input
                type="text"
                id="customer_name"
                name="customer_name"
                value={formData.customer_name}
                onChange={handleChange}
                required
                aria-required="true"
                autoComplete="name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="customer_email">
                Email Address <span className="required" aria-label="required">*</span>
              </label>
              <input
                type="email"
                id="customer_email"
                name="customer_email"
                value={formData.customer_email}
                onChange={handleChange}
                required
                aria-required="true"
                autoComplete="email"
                aria-describedby="email-help"
              />
              <small id="email-help" className="form-help">
                We'll send your order confirmation here
              </small>
              <HotmailDeliveryNotice email={formData.customer_email} />
            </div>

            <div className="form-group">
              <label htmlFor="customer_phone">
                Phone Number
              </label>
              <input
                type="tel"
                id="customer_phone"
                name="customer_phone"
                value={formData.customer_phone}
                onChange={handleChange}
                autoComplete="tel"
                aria-describedby="phone-help"
              />
              <small id="phone-help" className="form-help">
                Optional - for delivery updates
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="shipping_address_line1">
                Street Address <span className="required" aria-label="required">*</span>
              </label>
              <input
                type="text"
                id="shipping_address_line1"
                name="shipping_address_line1"
                value={formData.shipping_address_line1}
                onChange={handleChange}
                required
                aria-required="true"
                autoComplete="address-line1"
              />
            </div>

            <div className="form-group">
              <label htmlFor="shipping_address_line2">
                Apartment, Suite, etc. (Optional)
              </label>
              <input
                type="text"
                id="shipping_address_line2"
                name="shipping_address_line2"
                value={formData.shipping_address_line2}
                onChange={handleChange}
                autoComplete="address-line2"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="shipping_city">
                  City <span className="required" aria-label="required">*</span>
                </label>
                <input
                  type="text"
                  id="shipping_city"
                  name="shipping_city"
                  value={formData.shipping_city}
                  onChange={handleChange}
                  required
                  aria-required="true"
                  autoComplete="address-level2"
                />
              </div>

              <div className="form-group">
                <label htmlFor="shipping_province">
                  Province <span className="required" aria-label="required">*</span>
                </label>
                <select
                  id="shipping_province"
                  name="shipping_province"
                  value={formData.shipping_province}
                  onChange={handleChange}
                  required
                  aria-required="true"
                  autoComplete="address-level1"
                >
                  {provinces.map(prov => (
                    <option key={prov} value={prov}>{prov}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="shipping_postal_code">
                  Postal Code <span className="required" aria-label="required">*</span>
                </label>
                <input
                  type="text"
                  id="shipping_postal_code"
                  name="shipping_postal_code"
                  value={formData.shipping_postal_code}
                  onChange={handleChange}
                  required
                  aria-required="true"
                  autoComplete="postal-code"
                  placeholder="A1A 1A1"
                  pattern="[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d"
                  title="Please enter a valid Canadian postal code (e.g., A1A 1A1)"
                />
              </div>

              <div className="form-group">
                <label htmlFor="shipping_country">
                  Country
                </label>
                <select
                  id="shipping_country"
                  name="shipping_country"
                  value={formData.shipping_country}
                  onChange={handleChange}
                  autoComplete="country"
                >
                  <option value="Canada">Canada</option>
                </select>
              </div>
            </div>

            {/* Tier 2 Item 21 — Fulfillment picker (ship vs pickup) */}
            {bopisAvailable && (
              <div className="form-group" style={{ background: '#f8fafc', padding: 12, borderRadius: 6, border: '1px solid #e2e8f0' }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: 8 }}>Fulfillment</label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 6 }}>
                  <input type="radio" name="fulfillment" value="ship"
                         checked={fulfillmentMode === 'ship'} onChange={() => setFulfillmentMode('ship')} />
                  <span>Ship to my address</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 6 }}>
                  <input type="radio" name="fulfillment" value="pickup"
                         checked={fulfillmentMode === 'pickup'} onChange={() => setFulfillmentMode('pickup')} />
                  <span>Pick up in store (free, ready in ~24h)</span>
                </label>
                {fulfillmentMode === 'pickup' && (
                  <small style={{ display: 'block', marginTop: 6, color: '#64748b' }}>
                    We'll email you a pickup code when your order is ready. Items are held for 7 days.
                  </small>
                )}
              </div>
            )}

            {/* Tier 1 Item 13 — Ships-from indicator */}
            {shipsFrom?.warehouse && (
              <div style={{ marginTop: 4, marginBottom: 8, fontSize: 13, color: '#64748b' }}>
                📦 Ships from <strong>{shipsFrom.warehouse.name}</strong>
                {shipsFrom.warehouse.city && <> · {shipsFrom.warehouse.city}{shipsFrom.warehouse.province ? `, ${shipsFrom.warehouse.province}` : ''}</>}
                {shipsFrom.split && <> · <em>multiple shipments</em></>}
              </div>
            )}

            {/* Live shipping rates (Tier 1 Item 1). Hidden when no rates returned
                — that's the silent fallback to the flat rule. */}
            {(liveRates.length > 0 || liveRatesLoading || liveRatesErr) && (
              <div className="form-group">
                <label>Shipping method</label>
                {liveRatesLoading && <div style={{ fontSize: 13, color: '#64748b' }}>Loading live rates…</div>}
                {!liveRatesLoading && liveRatesErr && <div style={{ fontSize: 13, color: '#b91c1c' }}>{liveRatesErr} — using flat rate.</div>}
                {!liveRatesLoading && liveRates.length > 0 && (
                  <div style={{ display: 'grid', gap: 6, marginTop: 6 }}>
                    {liveRates.slice().sort((a, b) => a.total_cents - b.total_cents).map((r, i) => {
                      const id = `${r.provider}-${r.service_code}`;
                      const checked = selectedRate && selectedRate.provider === r.provider && selectedRate.service_code === r.service_code;
                      return (
                        <label key={i} style={{
                          display: 'flex', alignItems: 'center', gap: 10,
                          padding: '8px 12px', borderRadius: 6,
                          border: `1px solid ${checked ? 'var(--color-primary, #4f46e5)' : 'var(--border-color, #e2e8f0)'}`,
                          background: checked ? 'var(--bg-hover, #eef2ff)' : '#fff', cursor: 'pointer',
                        }}>
                          <input type="radio" name="shipping_rate" id={id}
                                 checked={!!checked}
                                 onChange={() => setSelectedRate(r)} />
                          <span style={{ flex: 1, fontSize: 14 }}>
                            <strong>{r.service_name}</strong>
                            <span style={{ color: '#64748b', fontSize: 12, marginLeft: 8 }}>
                              {r.provider}{r.eta_days ? ` · ${r.eta_days}-day` : ''}
                            </span>
                          </span>
                          <span style={{ fontWeight: 600 }}>${(r.total_cents / 100).toFixed(2)}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="order_notes">
                Order Notes (Optional)
              </label>
              <textarea
                id="order_notes"
                name="order_notes"
                value={formData.order_notes}
                onChange={handleChange}
                rows="3"
                placeholder="Any special instructions for your order?"
              />
            </div>

            {/* Tier 1 Item 7 — Net-30 / PO option for eligible B2B customers. */}
            {(net30.eligible || (sezzleEnabled && totals.total >= 35)) && totals.total > 0 && (
              <div className="form-group" style={{ background: '#f8fafc', padding: 12, borderRadius: 6, border: '1px solid #e2e8f0' }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: 8 }}>Payment method</label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 6 }}>
                  <input type="radio" name="payment_mode" value="card"
                         checked={paymentMode === 'card'} onChange={() => setPaymentMode('card')} />
                  <span>Pay by card now</span>
                </label>
                {net30.eligible && (
                  <>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 6 }}>
                      <input type="radio" name="payment_mode" value="net30"
                             checked={paymentMode === 'net30'} onChange={() => setPaymentMode('net30')} />
                      <span>Invoice me on Net-{net30.default_terms_days || 30} (PO)</span>
                    </label>
                    {paymentMode === 'net30' && (
                      <input
                        type="text" value={poNumber} onChange={e => setPoNumber(e.target.value)}
                        placeholder="PO number (optional)"
                        style={{ marginTop: 8, padding: 8, width: '100%' }} />
                    )}
                  </>
                )}
                {sezzleEnabled && totals.total >= 35 && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 6 }}>
                    <input type="radio" name="payment_mode" value="sezzle"
                           checked={paymentMode === 'sezzle'} onChange={() => setPaymentMode('sezzle')} />
                    <span>
                      Pay in 4 with <strong>Sezzle</strong>
                      <small style={{ display: 'block', color: '#6b7280' }}>
                        4 interest-free payments of ${(totals.total / 4).toFixed(2)}. You'll be redirected to Sezzle.
                      </small>
                    </span>
                  </label>
                )}
              </div>
            )}

            {/* Apple Pay / Google Pay via Stripe PaymentRequest (Tier 2 Item 24) */}
            {paymentMode === 'card' && paymentMode !== 'sezzle' && provider === 'stripe' && STRIPE_ENABLED && totals.total > 0 && walletEnabled && (
              <WalletPay
                amount={totals.total}
                currency="cad"
                country="CA"
                label="SilverGames order"
                userId={user?.id || null}
                enabled={walletEnabled}
                onSuccess={async ({ paymentIntentId }) => {
                  try {
                    setLoading(true);
                    await placeOrder({ paymentIntentId });
                  } catch (e) { setError(e.message || 'Order failed'); }
                  finally { setLoading(false); }
                }}
                onError={(err) => { setError(err.message || 'Wallet payment failed'); toast.error(err.message || 'Wallet payment failed'); }}
              />
            )}

            {/* Card input — provider-aware (Stripe Elements OR Helcim iframe). */}
            {paymentMode === 'card' && provider === 'stripe' && STRIPE_ENABLED && totals.total > 0 && (
              <div className="form-group">
                <label>
                  Card details <span className="required" aria-label="required">*</span>
                </label>
                <div style={{
                  border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)',
                  padding: 'var(--sp-3)', background: '#fff',
                }}>
                  <CardElement
                    options={{ style: { base: { fontSize: '16px', color: 'var(--text-dark)' } } }}
                    onChange={(ev) => setStripeReady(ev.complete)}
                  />
                </div>
                <small className="form-help">Test card: 4242 4242 4242 4242 · any future date · any CVC</small>
              </div>
            )}

            {/* Ontario Consumer Protection Act §25 disclosure: surface key
                contract terms before purchase. Plain language, linked policies. */}
            <div style={{
              fontSize: 12, color: '#555', background: '#f7f7f8',
              border: '1px solid #ececef', borderRadius: 8,
              padding: '10px 12px', marginBottom: 12, lineHeight: 1.5,
            }}>
              By placing this order you agree to our <a href="/terms" target="_blank" rel="noreferrer">Terms</a> and{' '}
              <a href="/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>. Sealed product:{' '}
              <strong>30-day returns</strong> if unopened. Singles & graded slabs are{' '}
              <strong>final sale</strong> unless damaged in transit. Tax shown is calculated based on your
              shipping province (HST/GST/PST). Shipping is tracked across Canada; delivery typically 2–5 business days.
            </div>

            {/* Quebec (Bill 96) — French-language commercial communications are
                required when contracting with Quebec consumers. Offer a
                machine-translated French version with a clear disclaimer.
                Setting `locale=fr` triggers any page using useTranslations()
                to fetch French strings from /api/translate. */}
            {formData.shipping_province === 'Quebec' && (
              <div role="alert" style={{
                fontSize: 12, color: '#7a4900',
                background: '#fff8e1', border: '1px solid #ffeaa7',
                borderRadius: 8, padding: '10px 12px', marginBottom: 12, lineHeight: 1.5,
              }}>
                <strong>Pour nos clients du Québec :</strong> nous offrons une traduction française automatique de notre boutique.{' '}
                <button
                  type="button"
                  onClick={() => { localStorage.setItem('locale', 'fr'); window.dispatchEvent(new Event('locale:change')); }}
                  style={{ background: 'transparent', border: 'none', textDecoration: 'underline', color: '#7a4900', cursor: 'pointer', font: 'inherit', padding: 0 }}
                >Passer en français →</button>
                <div style={{ marginTop: 4, fontSize: 11, opacity: 0.8 }}>
                  Traduction automatique. Pour le libellé juridiquement précis, l'original anglais fait foi.
                </div>
              </div>
            )}

            {/* Helcim flow: iframe handles card collection + auth itself, then
                triggers placeOrder via onSuccess. We render its own button and
                hide the standard submit. */}
            {paymentMode === 'card' && provider === 'helcim' && chargeAmount > 0 ? (
              <div className="form-group">
                {isDepositCheckout && (
                  <div style={{ background: '#fef3c7', border: '1px solid #b45309', padding: 10, borderRadius: 6, marginBottom: 10, fontSize: 13 }}>
                    <strong>Reservation deposit</strong> — you're authorizing ${depositTotal.toFixed(2)} now to reserve {depositItems.length} item{depositItems.length === 1 ? '' : 's'}. The balance (${(totals.total - depositTotal).toFixed(2)}) will be charged when stock ships.
                  </div>
                )}
                {hasMixedDepositCart && (
                  <div style={{ background: '#fee2e2', border: '1px solid #b91c1c', padding: 10, borderRadius: 6, marginBottom: 10, fontSize: 13 }}>
                    Your cart mixes regular items and deposit reservations. Please check out reservations separately so the deposit hold works correctly.
                  </div>
                )}
                <HelcimCardForm
                  amount={chargeAmount}
                  currency="CAD"
                  orderNumber={`web-${Date.now()}`}
                  preauth={isDepositCheckout}
                  onSuccess={async ({ transactionId }) => {
                    try { await placeOrder({ paymentIntentId: transactionId, provider: 'helcim', preauth: isDepositCheckout }); }
                    catch (e) { setError(e.message || 'Order placement failed'); toast.error(e.message || 'Order failed'); }
                  }}
                  onError={(e) => { setError(e.message || 'Payment failed'); }}
                />
              </div>
            ) : (
            <button
              type="submit"
              className="btn btn-primary btn-block"
              disabled={loading || (provider === 'stripe' && STRIPE_ENABLED && totals.total > 0 && !stripeReady)}
              aria-busy={loading}
            >
              {loading ? 'Processing…' : `Place Order — $${totals.total.toFixed(2)}`}
            </button>
            )}
          </form>
        </div>

        <div className="checkout-summary">
          <h2>Order Summary</h2>

          <div className="summary-items">
            {cart.map(item => {
              let mods = item.modifiers;
              if (typeof mods === 'string') { try { mods = JSON.parse(mods); } catch (_) { mods = null; } }
              return (
                <div key={item.id} className="summary-item">
                  <div style={{ flex: 1 }}>
                    <span className="item-name">
                      {item.name} × {item.quantity}
                    </span>
                    {mods && typeof mods === 'object' && Object.keys(mods).length > 0 && (
                      <ul style={{ listStyle: 'none', padding: '4px 0 0 12px', margin: 0, fontSize: 11, color: '#6b7280' }}>
                        {Object.entries(mods).map(([k, v]) => (
                          <li key={k}><em>{k}:</em> {v}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <span className="item-price">
                    ${(item.price * item.quantity).toFixed(2)}
                  </span>
                </div>
              );
            })}
          </div>

          <div className="summary-calculations">
            <div className="summary-row">
              <span>Subtotal:</span>
              <span>${totals.subtotal.toFixed(2)}</span>
            </div>

            <div className="summary-row">
              <span>Shipping:</span>
              <span>{totals.shipping === 0 ? 'FREE' : `$${totals.shipping.toFixed(2)}`}</span>
            </div>

            {totals.shipping === 0 && (
              <div className="free-shipping-notice">
                Free shipping applied
              </div>
            )}

            {totals.subtotal < 100 && (
              <div className="shipping-notice">
                Add ${(100 - totals.subtotal).toFixed(2)} more for free shipping!
              </div>
            )}

            <div className="summary-row">
              <span>Tax ({formData.shipping_province}):</span>
              <span>${totals.tax.toFixed(2)}</span>
            </div>

            {totals.couponDiscount > 0 && (
              <div className="summary-row" style={{ color: 'var(--color-success)' }}>
                <span>Coupon ({couponCode.toUpperCase()}):</span>
                <span>−${totals.couponDiscount.toFixed(2)}</span>
              </div>
            )}
            {totals.giftCardApplied > 0 && (
              <div className="summary-row" style={{ color: 'var(--color-success)' }}>
                <span>Gift card:</span>
                <span>−${totals.giftCardApplied.toFixed(2)}</span>
              </div>
            )}
            {totals.loyaltyDiscount > 0 && (
              <div className="summary-row" style={{ color: 'var(--color-success)' }}>
                <span>Loyalty points:</span>
                <span>−${totals.loyaltyDiscount.toFixed(2)}</span>
              </div>
            )}

            <div className="summary-total">
              <span>Total:</span>
              <span>${totals.total.toFixed(2)}</span>
            </div>
          </div>

          {welcomeCoupon && couponDiscount === 0 && (
            <div style={{
              marginTop: 12, padding: 10, background: 'rgba(39, 174, 96, 0.1)',
              border: '1px solid var(--color-success)', borderRadius: 'var(--radius-md)',
              fontSize: 13,
            }}>
              First time? Use code <strong>{welcomeCoupon.code}</strong> for {welcomeCoupon.label}.
              <button
                type="button"
                onClick={() => { setCouponCode(welcomeCoupon.code); setTimeout(applyCoupon, 50); }}
                style={{ marginLeft: 8, color: 'var(--color-success)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700 }}
              >Apply now →</button>
            </div>
          )}

          {/* Coupon (B2) + Tier 3 Item 35 stacking */}
          <div className="form-group" style={{ marginTop: 16 }}>
            <label htmlFor="coupon">Have a coupon?</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                id="coupon"
                type="text"
                value={couponCode}
                onChange={e => setCouponCode(e.target.value)}
                placeholder="CODE"
                style={{ flex: 1 }}
              />
              <button type="button" className="btn btn-secondary" onClick={applyCoupon}>Apply</button>
            </div>

            {stackedCoupons.map((sc, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, marginTop: 6, fontSize: 13, alignItems: 'center' }}>
                <span style={{ flex: 1, color: '#10b981' }}>+ {sc.code} (−${Number(sc.discount).toFixed(2)})</span>
                <button type="button" onClick={() => setStackedCoupons(prev => prev.filter((_, idx) => idx !== i))}
                  style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer' }}>Remove</button>
              </div>
            ))}

            {stackingFlagOn && couponDiscount > 0 && stackedCoupons.length < 3 && (
              <button
                type="button"
                onClick={async () => {
                  const code = window.prompt('Additional coupon code:');
                  if (!code) return;
                  try {
                    const r = await fetch('/api/coupons/validate', {
                      method: 'POST', headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ code, subtotal: getCartTotal() }),
                    });
                    const d = await r.json();
                    if (!r.ok || d.valid === false) throw new Error(d.error || 'Invalid coupon');
                    setStackedCoupons(prev => [...prev, { code: code.toUpperCase(), discount: Number(d.discount_amount || d.discount || 0) }]);
                    toast.success(`Added ${code.toUpperCase()} (saved an extra $${Number(d.discount_amount || 0).toFixed(2)} — final total recalculated at checkout)`);
                  } catch (e) { toast.error(e.message); }
                }}
                style={{ marginTop: 6, background: 'none', border: '1px dashed #cbd5e1', color: '#475569', padding: '4px 8px', cursor: 'pointer', fontSize: 12, borderRadius: 4 }}
              >+ Add another stackable coupon</button>
            )}
          </div>

          {/* Gift card (B3) */}
          <div className="form-group">
            <label htmlFor="giftcard">Have a gift card?</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                id="giftcard"
                type="text"
                value={giftCardCode}
                onChange={e => setGiftCardCode(e.target.value)}
                placeholder="GIFT-XXXXX"
                style={{ flex: 1 }}
              />
              <button type="button" className="btn btn-secondary" onClick={applyGiftCard}>Check</button>
            </div>
          </div>

          {/* Loyalty (B4) */}
          {user && availablePoints > 0 && (
            <div className="form-group">
              <label htmlFor="loyalty">
                Use loyalty points (max {availablePoints} = ${(availablePoints / 100).toFixed(2)} off)
              </label>
              <input
                id="loyalty"
                type="number"
                min="0"
                max={availablePoints}
                step="100"
                value={loyaltyPoints}
                onChange={e => setLoyaltyPoints(Math.min(Number(e.target.value), availablePoints))}
              />
              <small className="form-help">100 points = $1 off</small>
            </div>
          )}

          <div className="secure-checkout-badge">
            Secure Checkout
          </div>
        </div>
      </div>
    </div>
  );
}

export default EnhancedCheckout;
